import api from './api';
import { mockGenerateHashtags, mockBestTime } from '../utils/mockData';

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false';
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------- Mock helpers for image-based generation -----------------------
// Matches the backend MockContentProvider output shape exactly, so the UI
// needs no branching. Derives keywords from the image filename when useful.

const MOCK_HOOKS = {
  professional: {
    en: [
      'Here is what we learned this week:',
      'A small detail that made a big difference:',
      'The one question every leader should ask:',
      'We just shipped something we are proud of.',
    ],
    ar: [
      'هذا ما تعلمناه هذا الأسبوع:',
      'تفصيل صغير صنع فرقًا كبيرًا:',
      'سؤال يجب أن يطرحه كل قائد:',
    ],
  },
  casual: {
    en: [
      'Okay, this might be my new favorite thing.',
      'Quick check — are we doing this right?',
      'Guess what? We are back.',
    ],
    ar: [
      'يا جماعة، هذا الشيء رائع فعلاً.',
      'خمنوا ماذا؟ عدنا من جديد.',
    ],
  },
  sales: {
    en: [
      'Three reasons this might be the last one you need:',
      'If you have been waiting — this is your sign.',
      'Stop scrolling if you have ever wanted to try this.',
    ],
    ar: [
      'ثلاثة أسباب تجعل هذا الخيار الأخير:',
      'إذا كنت تنتظر — هذه إشارتك.',
    ],
  },
  funny: {
    en: [
      'I was today years old when I figured this out.',
      'POV: you clicked the wrong button (again).',
    ],
    ar: ['اكتشفت هذا اليوم للتو.', 'ضغطت الزر الخطأ (مرة أخرى).'],
  },
};

const MOCK_PLATFORM_TAGS = {
  instagram: ['#instagood', '#reels', '#explore'],
  facebook: ['#community', '#update'],
  linkedin: ['#leadership', '#business', '#growth'],
  tiktok: ['#fyp', '#foryou', '#trending'],
  x: ['#trending', '#news'],
};

const MOCK_CTAS = {
  en: {
    instagram: 'Tap the link in our bio.',
    facebook: 'Share this with someone who would love it.',
    linkedin: 'What has been your experience? Drop a comment.',
    tiktok: 'Follow for more.',
    x: 'Reply with your take 👇',
  },
  ar: {
    instagram: 'اضغط الرابط في البايو.',
    facebook: 'شاركنا رأيك في التعليقات.',
    linkedin: 'ما هي تجربتك؟ اترك تعليقًا.',
    tiktok: 'تابعنا للمزيد.',
    x: 'شاركنا رأيك 👇',
  },
};

function mockKeywordsFromFilename(name) {
  const map = {
    product: ['product', 'launch', 'design'],
    team: ['team', 'culture', 'people'],
    event: ['event', 'conference', 'live'],
    food: ['food', 'recipe', 'foodie'],
    travel: ['travel', 'wanderlust', 'explore'],
    launch: ['launch', 'announcement', 'new'],
  };
  const lower = (name || '').toLowerCase();
  for (const [k, v] of Object.entries(map)) {
    if (lower.includes(k)) return { matched: k, keywords: v };
  }
  return { matched: null, keywords: ['moment', 'inspiration'] };
}

function mockGenerateFromImage(file, { platform = 'instagram', tone = 'casual', language = 'en' }) {
  const { matched, keywords } = mockKeywordsFromFilename(file?.name || '');
  const isVideo = /^video\//i.test(file?.type || '');
  const kwHashtags = keywords.map((k) => `#${k}`);
  const hashtags = Array.from(new Set([...kwHashtags, ...(MOCK_PLATFORM_TAGS[platform] || [])])).slice(0, 10);

  const hookPool = (MOCK_HOOKS[tone] || MOCK_HOOKS.casual)[language] || MOCK_HOOKS.casual.en;
  const hooks = [...hookPool].sort(() => 0.5 - Math.random()).slice(0, 3);
  const hook = hooks[0];

  const body =
    language === 'ar'
      ? {
          product: 'نقدم لكم شيئًا عملنا عليه بعناية — جودة تستحق التجربة.',
          team: 'أفضل الأعمال تأتي عندما يجتمع الأشخاص المناسبون.',
          event: 'لحظات كهذه تذكرنا لماذا نحب ما نفعل.',
          food: 'طبق يستحق المشاركة.',
          travel: 'رحلة صغيرة بذكريات كبيرة.',
          launch: 'إطلاق جديد نفخر به.',
        }
      : {
          product: "Introducing something we've been working on — quality you can feel.",
          team: 'Great work happens when the right people come together.',
          event: 'Moments like this remind us why we do the work.',
          food: 'A plate worth sharing.',
          travel: 'A little trip, a big memory.',
          launch: 'A launch moment worth celebrating.',
        };
  const bodyText = body[matched] || (language === 'ar'
    ? 'مشاركة جزء بسيط من يومنا معكم.'
    : 'Sharing a small piece of our day with you.');

  const cta = (MOCK_CTAS[language] || MOCK_CTAS.en)[platform];
  const caption = `${hook}\n\n${bodyText}\n\n${cta}`;

  const altHook = hookPool[(hookPool.indexOf(hook) + 1) % hookPool.length];
  const suggestions = [
    `${altHook}\n\n${bodyText}`,
    language === 'ar'
      ? `تذكير هادئ: الأشياء الجميلة تستحق المشاركة.\n\n${cta}`
      : `A quiet reminder: good things are worth sharing.\n\n${cta}`,
  ];

  return {
    caption,
    hooks,
    hashtags,
    suggestions,
    cta,
    analysis: {
      objects: matched ? [matched, 'subject'] : ['subject', 'background'],
      scene: matched ? `${matched} photo` : 'general photo',
      context: matched || 'lifestyle',
      mood: tone === 'professional' ? 'serious' : tone === 'funny' ? 'fun' : 'casual',
      has_text: false,
      text: null,
      keywords,
      summary: `A ${matched || 'general'} ${isVideo ? 'video clip' : 'photo'} suitable for a ${platform} post.`,
      meta: {
        provider: 'mock-frontend',
        filename: file?.name || null,
        media_type: isVideo ? 'video' : 'image',
        media_url: null,
      },
    },
  };
}

function mockRewrite(caption, directive, { tone = 'casual', language = 'en', platform = 'instagram' }) {
  const trimmed = (caption || '').trim();
  let rewritten = trimmed;
  if (directive === 'shorten') {
    rewritten = trimmed.split(/[.!?\n]/)[0].slice(0, 140);
  } else if (directive === 'expand') {
    const add =
      language === 'ar'
        ? '\n\nنعتقد أن التفاصيل تصنع الفرق. هذا المنشور ليس مجرد كلمات — إنه قصة صغيرة نحب مشاركتها.'
        : "\n\nWe believe the details are what make the difference. This isn't just a post — it's a small story we wanted to share.";
    rewritten = trimmed + add;
  } else {
    const prefix =
      language === 'ar'
        ? (tone === 'sales' ? 'فرصة محدودة: ' : tone === 'professional' ? '— ' : '')
        : (tone === 'sales' ? 'Last call: ' : tone === 'professional' ? '— ' : '');
    rewritten = `${prefix}${trimmed}`;
  }

  const cta = (MOCK_CTAS[language] || MOCK_CTAS.en)[platform];
  return {
    caption: rewritten,
    hooks: [],
    hashtags: [],
    suggestions: [
      directive === 'expand' ? `${rewritten}\n\n${cta}` : rewritten.split(/[.!?\n]/)[0].slice(0, 140),
      rewritten,
    ],
    cta,
  };
}

// ---------- Public API ----------------------------------------------------

export const aiService = {
  /**
   * POST /ai/hashtags
   * @param {string} caption
   * @param {string} platform
   * @returns {Promise<{ hashtags: string[] }>}
   */
  async generateHashtags(caption, platform) {
    if (USE_MOCK) {
      await delay(900);
      return { hashtags: mockGenerateHashtags(caption, platform) };
    }
    const { data } = await api.post('/ai/hashtags', { caption, platform });
    return data;
  },

  /**
   * POST /ai/best-time
   */
  async getBestTime(platform, timezone) {
    if (USE_MOCK) {
      await delay(700);
      return mockBestTime(platform);
    }
    const { data } = await api.post('/ai/best-time', { platform, timezone });
    return data;
  },

  /**
   * POST /ai/generate-from-image (multipart)
   *
   * @param {File} file
   * @param {{ platform?: string, tone?: string, language?: string }} options
   * @returns {Promise<{ caption: string, hooks: string[], hashtags: string[], suggestions: string[], cta: string|null, analysis: object }>}
   */
  async generateFromImage(file, options = {}) {
    if (USE_MOCK) {
      await delay(1200);
      return mockGenerateFromImage(file, options);
    }
    const fd = new FormData();
    fd.append('media', file);
    // Backward-compatible field name (backend still accepts image).
    fd.append('image', file);
    for (const [k, v] of Object.entries(options)) {
      if (v !== undefined && v !== null && v !== '') fd.append(k, v);
    }
    const { data } = await api.post('/ai/generate-from-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /**
   * POST /ai/rewrite
   *
   * @param {string} caption
   * @param {'shorten'|'expand'|'rewrite'} directive
   * @param {{ platform?: string, tone?: string, language?: string }} options
   */
  async rewrite(caption, directive, options = {}) {
    if (USE_MOCK) {
      await delay(700);
      return mockRewrite(caption, directive, options);
    }
    const { data } = await api.post('/ai/rewrite', {
      caption,
      directive,
      ...options,
    });
    return data;
  },
};
